<template>
  <div class="search-box">
    <div class="search-input-field" :style="calculateSearchStyle()">
      <div class="search-icon">
        <img
          :src="
            props.isScene === true
              ? require('../assets/img/search_icon_black.png')
              : require('../assets/img/search_icon.png')
          "
          alt=""
        />
      </div>
      <input
        type="text"
        class="search-input"
        @focus="handleSearchFocus"
        @blur="handleSearchBlur"
        v-model="searchKeyword"
        :placeholder="placeholderText"
        :style="{
          backgroundColor: props.isScene === true && '#EBEBEB',
          color: props.isScene === true && 'black'
        }"
      />
    </div>
    <div class="search-result-box" :style="calculateSearchResultStyles()" v-if="showSearchResults">
      <div class="search-results">
        <div v-if="loading" class="loading-div">
          <div class="spinner-load"></div>
        </div>
        <div v-else>
          <div v-if="searchKeyword === '' && searchData.length === 0">Enter your keyword</div>
          <div v-if="searchData.length > 0">
            <div class="search-result" v-for="search in searchData" :key="search">
              <div class="search-head">{{ search.categoryName }}</div>
              <div
                class="search-data"
                :class="{ 'search-data-premium': isLensLocked(lenses.name) }"
                v-for="lenses in search.filteredLenses"
                :key="lenses.name"
                @click="handleSearchDataClick(lenses)"
              >
                <span>{{ lenses.name }}</span>
                <span class="badge-group" v-if="isLensLocked(lenses.name) || lenses.isNew">
                  <span v-if="lenses.isNew" class="new-badge">new</span>
                  <img
                    v-if="isLensLocked(lenses.name)"
                    :src="proLogo"
                    alt="PRO lens"
                    class="premium-badge"
                    @click.stop="emits('showPremiumAlert')"
                  />
                </span>
              </div>
            </div>
          </div>
          <div v-if="searchKeyword !== '' && searchData.length === 0">No results found.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import client from '@/api/client'
import { useLensesStore } from '@/stores/Lenses'
import { useScenesStore } from '@/stores/Scenes'
import { useUsersStore } from '@/stores/Users'
import { defineProps, defineEmits, ref, watch, onMounted, onUnmounted } from 'vue'
import { ampli } from '@/ampli'
import { useRouter } from 'vue-router'
import { createTypewriter } from '@/includes/helper'
import proLogo from '@/assets/img/PRO_logo.webp'
import {
  searchLensesByKeyword,
  mergeCategorizedResults,
  selectSearchResult
} from '@/includes/search'

const props = defineProps({
  isLeftSearch: Boolean,
  isScene: Boolean,
  isBrowse: Boolean,
  typewriterWords: {
    type: Array,
    default: () => []
  }
})

const lensStore = useLensesStore()
const sceneStore = useScenesStore()
const userStore = useUsersStore()

// Premium-lock badge only applies to the main lens comparison search, not scenes/browse
function isLensLocked(name) {
  if (props.isScene) return false
  return userStore.isLoggedInValue && !lensStore.isPremiumUser && lensStore.isLensPremium(name)
}

const searchData = ref([])
const debounceTimer = ref(null)
const placeholderText = ref('')
let typewriterInstance = null

onMounted(() => {
  // If typewriter words are provided, create a typewriter effect
  if (props.typewriterWords.length > 0) {
    typewriterInstance = createTypewriter({
      words: props.typewriterWords,
      baseText: 'Search ',
      onUpdate: text => {
        placeholderText.value = text
      }
    })

    // Starting the typewriter
    typewriterInstance.start()
  }
})

onUnmounted(() => {
  // If typewriter instance exists, stop it
  if (typewriterInstance) {
    typewriterInstance.stop()
  }
})

const emits = defineEmits(['focusEvent', 'blurEvent', 'searchDataClick', 'showPremiumAlert'])

const searchKeyword = ref('')
const showSearchResults = ref(false)
const loading = ref(false)

const router = useRouter()

let currentPathObject = router.currentRoute.value

const calculateSearchStyle = () => {
  if (props.isBrowse) {
    return {
      marginLeft: '0px',
      marginRight: '0px'
    }
  }

  return {
    marginLeft: props.isScene ? '8px' : props.isLeftSearch ? '48px' : '28px',
    marginRight: props.isScene ? '8px' : ''
  }
}

const calculateSearchResultStyles = () => {
  if (props.isBrowse || props.isScene) {
    return {
      marginLeft: '0px',
      marginRight: '0px',
      width: '100%'
    }
  }

  return {
    marginLeft: props.isLeftSearch ? '48px' : '28px',
    marginRight: '0px'
  }
}

const handleSearchFocus = () => {
  emits('focusEvent')

  showSearchResults.value = true
  if (props.isScene) {
    sceneStore.searchIsActive = true
  } else {
    lensStore.searchIsActive = true
  }
}

const handleSearchBlur = () => {
  emits('blurEvent')

  setTimeout(() => {
    if (!props.isBrowse) {
      searchKeyword.value = ''
      searchData.value = []
    }

    showSearchResults.value = false
    if (props.isScene) {
      sceneStore.searchIsActive = false
    } else {
      lensStore.searchIsActive = false
    }
  }, 200)
}

const removeDublicates = data => {
  let lensDict = {}

  // Iterate through the array
  data.forEach(lens => {
    if (!lensDict[lens.name]) {
      // If lens name not in dict, add it
      lensDict[lens.name] = { name: lens.name, isNew: lens.isNew }
    } else {
      // If lens name is already in dict, update isNew state
      if (lens.isNew) {
        lensDict[lens.name].isNew = true
      }
    }
  })

  // Convert the dictionary back to an array
  let uniqueLenses = Object.values(lensDict)

  return uniqueLenses
}

const handleSearchDataClick = lens => {
  if (props.isBrowse) {
    emits('searchDataClick', lens.name)
    searchKeyword.value = lens.name.toLowerCase()
    return
  }

  if (isLensLocked(lens.name)) {
    emits('showPremiumAlert')
  }

  const store = props.isScene ? sceneStore : lensStore
  selectSearchResult(store, lens, { side: props.isLeftSearch ? 'LEFT' : 'RIGHT' })

  searchKeyword.value = ''
}

const searchLens = async () => {
  const endPoint = props.isScene ? '/lens/scenes/search?searchQuery=' : '/lens/search?query='
  try {
    /*
      AMPLITUDE: Track lens search
      Triggers when a lens search API is called
    */
    ampli.lensSearched({
      query: searchKeyword?.value,
      page: currentPathObject?.fullPath
    })

    const response = await client.get(
      `${process.env.VUE_APP_BASE_URL}${endPoint}${searchKeyword.value}`
    )

    let unfilteredArray = []

    unfilteredArray = props.isScene ? response.data : response.data.lens
    let filteredArray = []

    unfilteredArray.forEach(array => {
      const lensObject = array.lenses?.map(lens => {
        return { name: lens.name, isNew: lens.isNew }
      })
      const filteredLenses = removeDublicates(lensObject)

      filteredArray.push({
        categoryName: array.name,
        filteredLenses
      })
    })

    const keywordMatches = searchLensesByKeyword(
      props.isScene ? sceneStore.data : lensStore.data,
      searchKeyword.value
    )

    const merged = mergeCategorizedResults(filteredArray, keywordMatches)

    // Free lenses first, then premium - both within each category and across
    // categories, so a category holding only premium lenses sorts after one
    // with at least one free lens
    if (props.isScene) {
      searchData.value = merged
    } else {
      const sortedCategories = merged.map(category => ({
        ...category,
        filteredLenses: [...category.filteredLenses].sort((a, b) => {
          const aPremium = lensStore.isLensPremium(a.name)
          const bPremium = lensStore.isLensPremium(b.name)
          if (aPremium !== bPremium) return aPremium ? 1 : -1
          return a.name.localeCompare(b.name)
        })
      }))

      searchData.value = sortedCategories.sort((a, b) => {
        const aPremium = lensStore.isLensPremium(a.filteredLenses[0]?.name)
        const bPremium = lensStore.isLensPremium(b.filteredLenses[0]?.name)
        if (aPremium !== bPremium) return aPremium ? 1 : -1
        return a.categoryName.localeCompare(b.categoryName)
      })
    }
  } catch (err) {
    console.log(err)
  } finally {
    loading.value = false
  }
}

watch(searchKeyword, async () => {
  emits('searchDataClick', searchKeyword.value)

  if (!searchKeyword.value || searchKeyword.value === '') {
    searchData.value = []
    return
  }
  if (debounceTimer.value) clearTimeout(debounceTimer.value)

  loading.value = true

  debounceTimer.value = setTimeout(() => {
    searchLens()
  }, 1000)
})
</script>

<style scoped>
.search-data {
  display: flex;
  gap: 5px;
  align-items: center;
}

.search-data-premium {
  opacity: 0.3;
}

.badge-group {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.3rem;
}

.premium-badge {
  display: inline-block;
  height: 0.8rem;
  width: auto;
  vertical-align: middle;
  cursor: pointer;
  opacity: 0.85;
}

.badge-group .new-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 0.85rem;
  margin-left: 0;
  padding: 0.35rem 0.3rem 0.25rem;
  font-size: 0.55rem;
  line-height: 1;
  box-sizing: border-box;
}

.scene-new-data {
  font-size: 12px;
}

.search-scene-data {
  font-size: 12px;
  display: flex;
  align-items: center;
}

.search-scene-data .new-badge {
  height: fit-content;
}

@media screen and (max-width: 720px) {
  .search-input-field {
    max-width: none;
  }

  .search-input {
    width: 100%;
    background: rgba(51, 152, 219, 0.2);
    padding: 12px 60px 12px 20px;
    border: none;
    outline: none;
    border-radius: 30px;
    font-size: 16px;
  }

  .search-result-box {
    width: 100%;
    max-width: none;
  }
}
</style>
